<!-- src/components/forms/completed/AnnualHealthAssessmentCompleted.vue -->
<template>
  <div class="space-y-16 animate-fadeIn">
    
    <!-- 1. Identity Verification -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Personnel Details</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">First Name</span>
          <span class="value uppercase">{{ submissionData.firstName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Last Name</span>
          <span class="value uppercase">{{ submissionData.lastName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Verified Email</span>
          <span class="value lowercase text-xs">{{ submissionData.email }}</span>
        </div>
      </div>
    </section>

    <!-- 2. Occupational History -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <ClockIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Occupational History</h3>
      </div>

      <div class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="MetricCard">
            <span class="num">{{ submissionData.sicknessDaysLast12Months || '0' }}</span>
            <span class="txt">Days Absence (Last 12m)</span>
          </div>
          <div class="MetricCard">
            <span class="num">{{ submissionData.sicknessDaysPrior12Months || '0' }}</span>
            <span class="txt">Days Absence (Prior 12m)</span>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4">
          <div v-for="q in occupationalQuestions" :key="q.key" class="MedicalRow">
            <div class="flex justify-between items-start gap-4">
              <p class="text-xs font-bold text-slate-700 leading-relaxed">{{ q.label }}</p>
              <span :class="getStatusClass(submissionData[q.key])" class="Badge">{{ formatYesNoNA(submissionData[q.key]) }}</span>
            </div>
            <div v-if="submissionData[q.detailsKey]" class="DetailsBox">
              <strong>Reported Details:</strong> {{ submissionData[q.detailsKey] }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Medical Conditions Grid -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <HeartIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">3. Clinical Condition Assessment</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-for="q in medicalQuestions" :key="q.key" class="MedicalRow bg-white">
          <div class="flex justify-between items-start gap-4">
            <p class="text-[11px] font-bold text-slate-600 leading-tight">{{ q.label }}</p>
            <span :class="getStatusClass(submissionData[q.key])" class="Badge">{{ formatYesNoNA(submissionData[q.key]) }}</span>
          </div>
          <div v-if="submissionData[q.detailsKey]" class="DetailsBox">
            <strong>Clinical Note:</strong> {{ submissionData[q.detailsKey] }}
          </div>
          <!-- Special logic for Symptoms Controlled confirmation -->
          <div v-if="q.key === 'symptomsControlled' && submissionData.symptomsControlledConfirmation" class="mt-2 flex items-center gap-2 text-[10px] font-black text-emerald-600 uppercase tracking-tighter bg-emerald-50 p-2 rounded-lg">
             <CheckBadgeIcon class="h-3 w-3" /> Confirmed Fit & Symptom Free
          </div>
        </div>
      </div>
    </section>

    <!-- 4. Additional Historical Context -->
    <section v-if="submissionData.otherConditions">
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <DocumentPlusIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">4. Historical Pertinence</h3>
      </div>
      <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm italic text-sm text-slate-600 leading-relaxed">
        "{{ submissionData.otherConditions }}"
      </div>
    </section>

    <!-- 5. Signature & Declaration -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <ShieldCheckIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Medical Attestation Verified</p>
          </div>
          <div class="p-6 bg-slate-900 rounded-[32px] border border-slate-800 max-w-md">
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Digital Declaration</p>
            <p class="text-[11px] text-slate-300 font-medium italic leading-relaxed">"I declare that the medical information provided is true and I have not withheld any material facts. I consent to the processing of this sensitive data."</p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[260px]">
          <p class="text-[9px] font-black uppercase text-slate-400 mb-4 tracking-widest">Authorized Seal</p>
          <img :src="submissionData.signatureDataUrl || submissionData.signatureUrl" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex flex-col items-center">
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">{{ submissionData.firstName }} {{ submissionData.lastName }}</span>
            <span class="text-[9px] font-bold text-slate-400 mt-1 uppercase">{{ formatDate(submissionData.submittedAt) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { 
  IdentificationIcon, ClockIcon, HeartIcon, 
  DocumentPlusIcon, ShieldCheckIcon, CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatYesNoNA: { type: Function, required: true },
  getStatusClass: { type: Function, required: true }
});

const occupationalQuestions = [
  { label: 'Employment terminated on grounds of ill health?', key: 'terminatedIllHealth', detailsKey: 'terminatedIllHealthDetails' },
  { label: 'Currently undergoing medical investigations?', key: 'medicalInvestigations', detailsKey: 'medicalInvestigationsDetails' },
  { label: 'Employment terminated under TW Act (Drugs & Alcohol)?', key: 'terminatedTWAct', detailsKey: 'terminatedTWActDetails' }
];

const medicalQuestions = [
  { label: 'a. Physical or mental disabilities?', key: 'disabilities', detailsKey: 'disabilityDetails' },
  { label: 'b. Health problems affecting joints (2 years)?', key: 'jointProblems', detailsKey: 'jointProblemDetails' },
  { label: 'c. Diabetes requiring insulin?', key: 'diabetes' },
  { label: 'd. Epilepsy or fits?', key: 'epilepsy' },
  { label: 'e. Blackouts or condition causing collapse?', key: 'blackouts' },
  { label: 'f. Chest pain or shortness of breath on exercise?', key: 'chestPain' },
  { label: 'g. Mobility difficulty on slopes/steps?', key: 'mobilityDifficulty' },
  { label: 'h. Difficulty looking over shoulder?', key: 'shoulderDifficulty' },
  { label: 'i. Eyesight difficulties (other than corrective)?', key: 'eyesightDifficulty' },
  { label: 'j. Difficulty hearing normal conversation?', key: 'hearingDifficulty' },
  { label: 'k. Medication causing dizziness/drowsiness?', key: 'drowsinessMedication' },
  { label: 'l. Illness related to alcohol (last 12m)?', key: 'alcoholIllness' },
  { label: 'm. Used hand-held vibrating tools?', key: 'vibratingTools', detailsKey: 'vibratingToolsDetails' },
  { label: 'n. Numbness/tingling after using vibration?', key: 'fingerNumbness' },
  { label: 'o. Numbness or tingling at any other time?', key: 'fingerNumbnessOther' },
  { label: 'p. Wake at night with pain/numbness in wrist?', key: 'wakeWithPain' },
  { label: 'q. Fingers gone white on cold exposure?', key: 'fingersGoneWhite' },
  { label: 'r. Change in tolerance to working in cold?', key: 'coldToleranceChange' },
  { label: 's. Problems in hands or arms?', key: 'handArmProblems' },
  { label: 't. Difficulty picking up small objects?', key: 'difficultySmallObjects' },
  { label: 'u. Health change since last assessment?', key: 'healthChange', detailsKey: 'healthChangeDetails' },
  { label: 'v. Current symptoms well controlled?', key: 'symptomsControlled', detailsKey: 'symptomsControlledDetails' }
];
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }

.MetricCard { @apply bg-white p-6 rounded-[24px] border border-slate-200 flex items-center gap-4 shadow-sm; }
.MetricCard .num { @apply text-2xl font-black text-[#0A1630]; }
.MetricCard .txt { @apply text-[10px] font-black uppercase text-slate-400 tracking-widest leading-tight; }

.MedicalRow { @apply p-6 border border-slate-100 rounded-3xl transition-all hover:border-[#20FCF1]/30 shadow-sm; }
.Badge { @apply px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex-shrink-0; }
.DetailsBox { @apply mt-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-[11px] text-red-800 leading-relaxed; }

.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>